/**
 * ContentProgressPanel Component
 *
 * Shows real-time progress during schema application.
 */

import { Progress, Alert } from "antd";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

interface ContentProgressPanelProps {
	processed: number;
	total: number;
	status: "idle" | "running" | "completed" | "failed";
	errorMessage?: string;
}

export function ContentProgressPanel({
	processed,
	total,
	status,
	errorMessage,
}: ContentProgressPanelProps) {
	const percent = total > 0 ? Math.round((processed / total) * 100) : 0;

	const getStatusIcon = () => {
		switch (status) {
			case "running":
				return <Loader2 className="animate-spin text-blue-500 dark:text-blue-400" size={20} />;
			case "completed":
				return <CheckCircle className="text-green-500 dark:text-green-400" size={20} />;
			case "failed":
				return <XCircle className="text-red-500 dark:text-red-400" size={20} />;
			default:
				return null;
		}
	};

	const getProgressStatus = () => {
		switch (status) {
			case "completed":
				return "success";
			case "failed":
				return "exception";
			default:
				return "active";
		}
	};

	return (
		<div className="p-4 bg-muted rounded-lg space-y-3">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					{getStatusIcon()}
					<span className="font-medium">
						{status === "running" && "Processing..."}
						{status === "completed" && "Completed"}
						{status === "failed" && "Failed"}
					</span>
				</div>
				<span className="text-sm text-muted-foreground">
					{processed} / {total} items
				</span>
			</div>

			<Progress
				percent={percent}
				status={getProgressStatus()}
				showInfo={true}
			/>

			{status === "failed" && errorMessage && (
				<Alert
					type="error"
					message="Error"
					description={errorMessage}
					showIcon
				/>
			)}

			{status === "completed" && (
				<Alert
					type="success"
					message="Schema Applied Successfully"
					description={`Successfully processed ${processed} items.`}
					showIcon
				/>
			)}
		</div>
	);
}
